// Layer 3 (Transformation) — route tabs and copy for the M11 downtime surface.

import { CAUSE_CATEGORIES, type CauseCategoryId } from '../maintenance/downtime.seed';
import type { DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';
import { formatCurrency } from '@/versions/v1/modules/capacity/Scene.transform';
import { causeKpis } from './Causes.transform';
import { plannedRatioRead } from './PlannedRatio.transform';
import { reliabilityKpis, reliabilityRows } from './Reliability.transform';
import { shiftCorrelationRead } from './ShiftCorrelation.transform';
import { valueLostKpis } from './ValueLost.transform';

export type DowntimeTab = 'pareto' | 'mtbf' | 'ratio' | 'shift' | 'value';

export const DOWNTIME_TABS: readonly { id: DowntimeTab; label: string }[] = [
  { id: 'pareto', label: 'Pareto — causes' },
  { id: 'mtbf', label: 'MTBF / MTTR' },
  { id: 'ratio', label: 'Planned vs unplanned' },
  { id: 'shift', label: 'Shift correlation' },
  { id: 'value', label: 'Value lost' },
];

export function isDowntimeTab(value: string | null): value is DowntimeTab {
  return value === 'pareto' || value === 'mtbf' || value === 'ratio' || value === 'shift' || value === 'value';
}

/** Validates a cause query against both the taxonomy and the active event cut. */
export function resolveDowntimeCause(
  rows: readonly DowntimeEvent[],
  value: string | null,
): CauseCategoryId | undefined {
  if (!value || !CAUSE_CATEGORIES.some((cause) => cause.id === value)) return undefined;
  return rows.some((event) => event.causeCategory === value) ? value as CauseCategoryId : undefined;
}

/** Each tab's headline, set beside its name in the tab row so the drill-downs
 *  carry their number before they are opened. Pareto and Value lost cut the
 *  selected window, as their tabs do; the other three read the full log, as
 *  theirs do. */
export function downtimeTabHeadlines(
  selectedRows: readonly DowntimeEvent[],
  allRows: readonly DowntimeEvent[],
): Readonly<Record<DowntimeTab, string>> {
  const reliability = reliabilityKpis(reliabilityRows(allRows));
  return {
    pareto: formatHours(causeKpis(selectedRows).unplannedHours),
    mtbf: `${reliability.atTargetCount} of ${reliability.classCount}`,
    ratio: `${Math.round(plannedRatioRead(allRows).plannedPct)}%`,
    shift: shiftCorrelationRead(allRows).busiestCrew.panel,
    value: formatCurrency(valueLostKpis(selectedRows).total),
  };
}
